import React, { Component } from "react";
import PropTypes from "prop-types";
import Popover from "./ui/Popover";
import Content from "./Content";
import EditContentInline from "./EditContentInline";
import { Scrollbars } from "react-custom-scrollbars";
import * as sheetActions from "../actions/sheets";
import SortContainer from "./SortContainer";
import { ItemTypes } from "../constants";
import { save } from "../actions/content";

export default class ContentPicker extends Component {
  static propTypes = {
    types: PropTypes.array,
    value: PropTypes.array,
    store: PropTypes.array,
    onChange: PropTypes.func,
    dispatch: PropTypes.func,
  };

  static defaultProps = {
    limit: 0,
    create: false,
  };

  static childContextTypes = {
    type: PropTypes.object,
  };

  formRefs = {};

  constructor(props) {
    super(props);
    this.state = {
      enabled: this.canPick(props),
      fieldsToValidate: [],
      filter: "",
      needsAsyncValidate: false,
      changedContent: {},
    };
  }

  getChildContext() {
    const { type } = this.props;
    return { type };
  }

  componentWillReceiveProps(props) {
    const { items, value, onChange } = props;
    if (!items || !items.length) return;

    // remove items if they no longer exist
    const newValue = value
      .slice()
      .filter((id) => !!props.items.filter((model) => model.id === id).length);
    if (newValue.length !== value.length) {
      onChange(newValue);
    }
  }

  canPick({ value, limit }) {
    const content = this.availableContent();
    if (content.length < 1) return false;
    if (!limit || limit === 0) return true;
    return value.length < limit;
  }

  canCreate() {
    return this.props.create;
  }

  availableContent() {
    const { store, value } = this.props;
    return store.filter((item) => value.indexOf(item.id) < 0);
  }

  filterContent = (content) => {
    const { filter } = this.state;
    const filterValue = filter.toLowerCase();

    for (const key in content) {
      const value = (content[key] || "").toString().toLowerCase();
      if (value.indexOf(filterValue) > -1) return true;
    }

    return false;
  };

  onAdd = (e) => {
    e.preventDefault();
    this.refs.picker.setState({ target: e.target, showing: true });
  };

  onDone = (e) => {
    e && e.preventDefault();
    this.refs.picker.setState({ showing: false });
  };

  onCreate = (type) => {
    const id = Math.random().toString(36).substr(2, 9);
    const item = { id, active: true, _new: true, _type: type };
    this.refs.typePicker.setState({ showing: false });
    this.setState(
      (prevState) => ({
        changedContent: {
          ...prevState.changedContent,
          [id]: item,
        },
      }),
      () => this.onPick(item)
    );
    this.triggerValidation();
  };

  onPickNew = (e) => {
    e.preventDefault();
    this.refs.typePicker.setState({ target: e.target, showing: true });
  };

  onPick = (item) => {
    const { value, onChange, limit, store } = this.props;
    const newValue = [...value, item.id];
    onChange && onChange(newValue);
    if (newValue.length >= limit && limit > 0) {
      this.onDone();
    }
    if (newValue.length >= store.length) {
      this.onDone();
    }
  };

  onSaveNew = (item, content) => {
    this.onRemove(item);
    this.onPick(content);
  };

  onRemove = (item) => {
    const { value, onChange } = this.props;
    const { changedContent } = this.state;

    if (changedContent[item.id]) {
      this.setState((prevState) => {
        const changedContent = { ...prevState.changedContent };
        delete changedContent[item.id];
        return { changedContent };
      });
    }

    const index = value.indexOf(item.id);
    const newValue = [...value.slice(0, index), ...value.slice(index + 1)];
    onChange && onChange(newValue);
  };

  onSort = (items) => {
    const { onChange } = this.props;
    onChange && onChange(items.map((model) => model.id));
  };

  onContentChange = (value) => {
    const { changedContent } = this.state;
    this.triggerValidation();
    this.setState((prevState) => ({
      changedContent: {
        ...prevState.changedContent,
        [value.id]: value,
      },
    }));
  };

  onFilter = (e) => {
    const filter = e.target.value;
    this.setState({ filter });
  };

  saveChangedContent = async () => {
    const { dispatch, onChange, value } = this.props;
    const { changedContent } = this.state;
    const newValue = await Promise.all(
      value.map(async (id) => {
        if (!changedContent[id]) return id;
        const { _new, _type, id: noop, ...data } = changedContent[id];
        const {
          data: { content },
        } = await dispatch(save(_new ? data : { ...data, id }, _type));
        return content.id;
      })
    );
    onChange(newValue);
    this.setState({ changedContent: {} });
  };

  validate = async () => {
    const { fieldsToValidate } = this.state;
    // validate sub-content collections
    await Promise.all(fieldsToValidate.map((field) => field.validate()));
    // validate sub-fields
    Object.keys(this.formRefs).map((key) => {
      const form = this.formRefs[key];
      if (!form || !form.validate) return true;
      const value = form.getValue();
      if (!value) {
        throw new Error("Validation Failed");
      }
      return true;
    });
    // save changed content
    await new Promise((resolve, reject) => {
      this.setState({ fieldsToValidate: [] }, () => {
        this.saveChangedContent().then(resolve).catch(reject);
      });
    });
    this.setState({ needsAsyncValidate: false });
  };

  validateOnSubmit = (component) => {
    this.triggerValidation();
    this.setState((prevState) => ({
      fieldsToValidate: [...prevState.fieldsToValidate, component],
    }));
  };

  triggerValidation = () => {
    const { needsAsyncValidate } = this.state;
    const {
      fieldContext: { validateOnSubmit },
    } = this.props;

    if (!needsAsyncValidate) {
      this.setState({ needsAsyncValidate: true }, () => validateOnSubmit(this));
    }
  };

  renderItem = (item) => {
    return (
      <Content
        key={item.id}
        content={item}
        type={item._type}
        onClick={this.onPick.bind(this, item)}
      />
    );
  };

  renderValue = (item) => {
    return (
      <EditContentInline
        key={item.id || item._new}
        model={item}
        type={item._type}
        formRef={(c) => (this.formRefs[item.id || item._new] = c)}
        fieldContext={{
          validateOnSubmit: this.validateOnSubmit,
        }}
        onChange={this.onContentChange}
        onRemove={this.onRemove.bind(null, item)}
      />
    );
  };

  render() {
    const { value, types, store } = this.props;
    const { changedContent, filter } = this.state;
    const available = this.availableContent();
    const canPick = this.canPick(this.props);

    return (
      <div>
        <div className="panel content-picker">
          <SortContainer
            itemType={ItemTypes.PICKER}
            items={value.map(
              (id) =>
                store.find((model) => model.id === id) || changedContent[id]
            )}
            didSort={this.onSort}
            renderItem={this.renderValue}
          />
          <div className="panel-buttons">
            {canPick && <button onClick={this.onAdd}>Add</button>}
            {this.canCreate() && (
              <button onClick={this.onPickNew}>Create new</button>
            )}
          </div>
        </div>
        <Popover ref="picker">
          <Scrollbars autoHide autoHeight autoHeightMax={400}>
            <div className="picker-search">
              <input
                type="search"
                placeholder="Search"
                value={filter}
                onChange={this.onFilter}
              />
            </div>
            <ul className="list picker-list">
              {available.filter(this.filterContent).map(this.renderItem)}
            </ul>
          </Scrollbars>
          <div className="actions">
            <button className="done" onClick={this.onDone}>
              Done
            </button>
          </div>
        </Popover>
        <Popover ref="typePicker">
          <ul className="list picker-list">
            {types.map((type) => (
              <div key={type.id} className="content-row">
                <div className="content-item">
                  <div
                    className="main-action"
                    onClick={() => this.onCreate(type)}
                  >
                    <div className="list-title">{type.name}</div>
                  </div>
                </div>
              </div>
            ))}
          </ul>
        </Popover>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/MultiContentPicker.js